import { useState, useEffect } from "react";
import { invoicesAPI, exportAPI } from "../lib/api";
import { formatDate } from "../lib/dateUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  FileText,
  Filter,
  User,
  Calendar,
  Printer,
  CheckCircle,
  Send,
  Download,
} from "lucide-react";

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await invoicesAPI.getAll();
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.updateStatus(invoiceId, newStatus);
      toast.success("Invoice status updated");
      fetchInvoices();
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({ ...selectedInvoice, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const viewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailOpen(true);
  };

  const handleExport = async () => {
    try {
      toast.info("Preparing export...");
      await exportAPI.download("invoices");
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      sent: "bg-purple-100 text-purple-800 border-purple-300",
      paid: "bg-green-100 text-green-800 border-green-300",
    };
    return (
      <Badge className={`${styles[status] || styles.pending} rounded-none uppercase text-xs font-bold tracking-wide border`}>
        {status}
      </Badge>
    );
  };

  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch =
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPending = invoices
    .filter((i) => i.status === "pending" || i.status === "sent")
    .reduce((sum, i) => sum + i.total_amount, 0);
  const totalPaid = invoices
    .filter((i) => i.status === "paid")
    .reduce((sum, i) => sum + i.total_amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="invoices-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Invoices
          </h1>
          <p className="text-slate-500">{filteredInvoices.length} invoices</p>
        </div>
        <div className="flex gap-4 items-center">
          <Card className="border-slate-200 px-4 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Pending</p>
            <p className="font-heading font-bold text-xl text-amber-600">
              ${totalPending.toLocaleString()}
            </p>
          </Card>
          <Card className="border-slate-200 px-4 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Collected</p>
            <p className="font-heading font-bold text-xl text-green-600">
              ${totalPaid.toLocaleString()}
            </p>
          </Card>
          <Button variant="outline" onClick={handleExport} className="rounded-sm" data-testid="export-invoices-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="invoices-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="invoices-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No invoices found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="border-slate-200 hover:border-slate-400 transition-colors">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-slate-800 rounded-sm">
                      <FileText className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-heading font-bold text-lg text-slate-900">
                          {invoice.invoice_number}
                        </h3>
                        {getStatusBadge(invoice.status)}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {invoice.customer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(invoice.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Total</p>
                      <p className="font-heading font-bold text-2xl text-slate-900">
                        ${invoice.total_amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewInvoice(invoice)}
                        className="rounded-sm"
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        View
                      </Button>
                      {invoice.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(invoice.id, "sent")}
                          className="bg-purple-500 hover:bg-purple-600 text-white rounded-sm"
                          data-testid={`send-invoice-${invoice.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </Button>
                      )}
                      {invoice.status === "sent" && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(invoice.id, "paid")}
                          className="bg-green-500 hover:bg-green-600 text-white rounded-sm"
                          data-testid={`mark-paid-${invoice.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invoice Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-heading font-bold text-2xl text-blue-500">POHLIHENG</h2>
                  <p className="text-sm text-slate-500">Excavator Repair Services</p>
                </div>
                <div className="text-right">
                  {getStatusBadge(selectedInvoice.status)}
                  <p className="text-sm text-slate-500 mt-1">
                    {formatDate(selectedInvoice.created_at)}
                  </p>
                </div>
              </div>

              {/* Customer */}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Bill To</p>
                <p className="font-semibold text-slate-900">{selectedInvoice.customer_name}</p>
              </div>

              {/* Job Description */}
              {selectedInvoice.job_description && (
                <div className="border-t border-slate-200 pt-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Work Performed</p>
                  <p className="text-slate-700">{selectedInvoice.job_description}</p>
                </div>
              )}

              {/* Line Items */}
              <div className="border-t border-slate-200 pt-4">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                      <th className="text-left pb-2">Description</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Rate</th>
                      <th className="text-right pb-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b border-slate-100">
                      <td className="py-2">Labor ({selectedInvoice.labor_hours || 0} hours)</td>
                      <td className="text-right py-2">{selectedInvoice.labor_hours || 0}</td>
                      <td className="text-right py-2">${selectedInvoice.labor_rate || 75}</td>
                      <td className="text-right py-2 font-semibold">
                        ${selectedInvoice.labor_total?.toFixed(2)}
                      </td>
                    </tr>
                    {(selectedInvoice.parts_breakdown || []).map((part, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-2">{part.part_name}</td>
                        <td className="text-right py-2">{part.quantity}</td>
                        <td className="text-right py-2">${part.unit_price}</td>
                        <td className="text-right py-2 font-semibold">
                          ${(part.quantity * part.unit_price).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Labor Subtotal</span>
                  <span className="font-semibold">${selectedInvoice.labor_total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Parts Subtotal</span>
                  <span className="font-semibold">${selectedInvoice.parts_total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="font-bold text-lg">Total Due</span>
                  <span className="font-heading font-bold text-2xl text-blue-600">
                    ${selectedInvoice.total_amount?.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  className="flex-1 rounded-sm"
                  onClick={() => window.print()}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                {selectedInvoice.status === "pending" && (
                  <Button
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white rounded-sm"
                    onClick={() => handleStatusChange(selectedInvoice.id, "sent")}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Mark as Sent
                  </Button>
                )}
                {selectedInvoice.status === "sent" && (
                  <Button
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-sm"
                    onClick={() => handleStatusChange(selectedInvoice.id, "paid")}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
